import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Group,
  Modal,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Textarea,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconInfoCircle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import type {
  PaymentRequirement,
  PaymentRequirementTarget,
  Student,
} from '@pta/types';
import { upsertRequirementTarget } from '../api/payment-requirements';
import { ApiError } from '../api/client';
import { useMoneyFormatter } from '../util/money';

interface RequirementTargetFormModalProps {
  opened: boolean;
  onClose: () => void;
  requirement: PaymentRequirement;
  target: PaymentRequirementTarget | null;
  groupStudents: Student[];
  existingTargets: PaymentRequirementTarget[];
  onSaved: () => void;
}

const fullSpan = { gridColumn: '1 / -1' };

export function RequirementTargetFormModal({
  opened,
  onClose,
  requirement,
  target,
  groupStudents,
  existingTargets,
  onSaved,
}: RequirementTargetFormModalProps) {
  const { t } = useTranslation();
  const { money } = useMoneyFormatter();

  const isEdit = target !== null;

  const [studentId, setStudentId] = useState<string | null>(null);
  const [amount, setAmount] = useState<number | ''>('');
  const [waived, setWaived] = useState(false);
  const [waiveReason, setWaiveReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!opened) return;
    if (target) {
      setStudentId(String(target.studentId));
      setAmount(Number(target.expectedAmount));
      setWaived(target.waived);
      setWaiveReason(target.waiveReason ?? '');
    } else {
      setStudentId(null);
      setAmount(Number(requirement.amount));
      setWaived(false);
      setWaiveReason('');
    }
  }, [opened, target, requirement]);

  const availableStudents = useMemo(() => {
    if (isEdit) {
      const s = groupStudents.find((st) => st.id === target!.studentId);
      return s
        ? [{ value: String(s.id), label: `${s.lastName} ${s.firstName}` }]
        : [
            {
              value: String(target!.studentId),
              label: `#${target!.studentId}`,
            },
          ];
    }
    const taken = new Set(existingTargets.map((x) => x.studentId));
    return groupStudents
      .filter((s) => !taken.has(s.id))
      .map((s) => ({
        value: String(s.id),
        label: `${s.lastName} ${s.firstName}`,
      }));
  }, [isEdit, target, groupStudents, existingTargets]);

  const validationError = (() => {
    if (!studentId) return null;
    if (typeof amount !== 'number' || amount < 0) {
      return t('requirements.target_err_amount', {
        ns: 'fe',
        defaultValue: 'Az összeg nem lehet negatív.',
      });
    }
    if (waived && waiveReason.trim() === '') {
      return t('requirements.target_err_waive_reason', {
        ns: 'fe',
        defaultValue: 'Waived=igen esetén az indok kötelező.',
      });
    }
    return null;
  })();

  const canSave =
    !!studentId && typeof amount === 'number' && validationError === null;

  const handleSave = async () => {
    if (!canSave || typeof amount !== 'number' || !studentId) return;
    setSaving(true);
    try {
      const amountStr = amount.toFixed(2).replace(/\.00$/, '');
      await upsertRequirementTarget(requirement.id, {
        studentId: Number(studentId),
        expectedAmount: amountStr,
        waived,
        waiveReason: waived ? waiveReason : null,
      });
      notifications.show({
        color: 'green',
        message: isEdit
          ? t('requirements.target_updated', {
              ns: 'fe',
              defaultValue: 'Target módosítva',
            })
          : t('requirements.target_created', {
              ns: 'fe',
              defaultValue: 'Target hozzáadva',
            }),
      });
      onSaved();
    } catch (e) {
      notifications.show({
        color: 'red',
        message: e instanceof ApiError ? e.message : String(e),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
      title={
        isEdit
          ? t('requirements.target_edit', {
              ns: 'fe',
              defaultValue: 'Target szerkesztése',
            })
          : t('requirements.target_new', {
              ns: 'fe',
              defaultValue: 'Új target hozzáadása',
            })
      }
      size="lg"
    >
      <Stack>
        {!isEdit && availableStudents.length === 0 && (
          <Alert color="yellow" variant="light" icon={<IconInfoCircle size={16} />}>
            {t('requirements.target_no_available_students', {
              ns: 'fe',
              defaultValue:
                'A csoport minden aktív tagja már target-tel rendelkezik ezen az előíráson.',
            })}
          </Alert>
        )}

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" verticalSpacing="sm">
          <Select
            label={t('requirements.target_student', {
              ns: 'fe',
              defaultValue: 'Diák',
            })}
            data={availableStudents}
            value={studentId}
            onChange={setStudentId}
            searchable
            disabled={isEdit}
            required
          />

          <NumberInput
            label={t('requirements.target_amount', {
              ns: 'fe',
              defaultValue: 'Várt összeg',
            })}
            description={`Előírás alap-összege: ${money(requirement.amount)}`}
            value={amount}
            onChange={(v) => setAmount(typeof v === 'number' ? v : '')}
            min={0}
            decimalScale={2}
            thousandSeparator=" "
            required
          />

          <Checkbox
            style={fullSpan}
            label={t('requirements.target_waived', {
              ns: 'fe',
              defaultValue: 'Mentesítve (waived)',
            })}
            checked={waived}
            onChange={(e) => {
              setWaived(e.currentTarget.checked);
              if (!e.currentTarget.checked) setWaiveReason('');
            }}
          />

          {waived && (
            <Textarea
              style={fullSpan}
              label={t('requirements.target_waive_reason', {
                ns: 'fe',
                defaultValue: 'Mentesítés indoka',
              })}
              value={waiveReason}
              onChange={(e) => setWaiveReason(e.currentTarget.value)}
              autosize
              minRows={2}
              maxLength={500}
              required
              error={
                waived && waiveReason.trim() === ''
                  ? t('requirements.target_err_waive_reason', {
                      ns: 'fe',
                      defaultValue: 'Waived=igen esetén az indok kötelező.',
                    })
                  : null
              }
            />
          )}
        </SimpleGrid>

        <Group justify="end">
          <Button variant="default" onClick={onClose} disabled={saving}>
            {t('common.cancel', { ns: 'common', defaultValue: 'Mégse' })}
          </Button>
          <Button onClick={handleSave} loading={saving} disabled={!canSave}>
            {t('common.save', { ns: 'common', defaultValue: 'Mentés' })}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
